import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyledToolbarButton, StyledTooltip } from '../../../../shared/styles/components/integrations.styles';
import { useEscapeKey, useKeyboardHandler } from '../../../../shared/utils/keyboard.utils';

import { BomFormValues } from '../../../bom/types/bom.types';
import CloneBomModal from '../../../bom/components/BomComponent';
import { ToastService } from '../../../../services/toast.service';
import VersionsDropdown from '../../../bom/components/VersionsDropdown';
import { formatBackendDateForDisplay } from '../../../../shared/utils/fiscal-year.utils';
import historyIcon from '@ingka/ssr-icon/paths/history';
import styled from '@emotion/styled';
import { useBomVersions } from '../../../bom/hooks/useBomVersions';
import { useForm } from 'react-hook-form';
import { useParentItemsFromSelectedSubItems } from '../../state/selection.state';
import { useQueryClient } from 'react-query';

export const BomToolbarButton: React.FC = () => {
  const selectedItems = useParentItemsFromSelectedSubItems(true);
  const nonVersionedItems = selectedItems.filter(item => !item.isVersionedItem);
  const selectedItem = nonVersionedItems.length > 0 ? nonVersionedItems[0] : undefined;
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCloning, setIsCloning] = useState(false);

  const [operationInProgress, setOperationInProgress] = useState(false);
  const [operationType, setOperationType] = useState<'fetching' | 'switching' | 'cloning' | 'clearing' | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const { reset } = useForm<BomFormValues>({
    defaultValues: {
      bomValidFrom: null
    }
  });

  const {
    versions: bomVersionHistory,
    currentVersion,
    isLoadingVersions,
    isLoadingVersionChange,
    isCloning: isApiCloning,
    error,
    switchVersion: handleVersionChange,
    clearVersion: handleClearBomVersion,
    cloneVersion,
    fetchVersions,
    clearError
  } = useBomVersions(selectedItem?.itemNo);

  useEffect(() => {
    const isLoading = isLoadingVersions || isLoadingVersionChange || isApiCloning || isCloning;

    let type: 'fetching' | 'switching' | 'cloning' | 'clearing' | null = null;
    if (isLoadingVersions) type = 'fetching';
    else if (isLoadingVersionChange) type = 'switching';
    else if (isApiCloning || isCloning) type = 'cloning';

    setOperationInProgress(isLoading);
    setOperationType(isLoading ? type : null);
  }, [isLoadingVersions, isLoadingVersionChange, isApiCloning, isCloning]);

  const handleKeyDown = useKeyboardHandler();

  const closeModalsCallback = useCallback(() => {
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
    }
    if (isModalOpen) {
      setIsModalOpen(false);
    }
  }, [isDropdownOpen, isModalOpen]);

  useEscapeKey(closeModalsCallback, isDropdownOpen || isModalOpen);

  useEffect(() => {
    if (error) {
      clearError();
    }
  }, [selectedItem?.itemNo, clearError, error]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggleDropdown = useCallback(() => {
    if (!isDropdownOpen && selectedItem?.itemNo) {
      fetchVersions();
    }
    setIsDropdownOpen(!isDropdownOpen);
  }, [isDropdownOpen, selectedItem?.itemNo, fetchVersions]);

  const handleOpenCloneModal = useCallback(() => {
    setIsDropdownOpen(false);
    setIsModalOpen(true);
    reset({ bomValidFrom: null });
  }, [reset]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onSubmit = useCallback(async (data: BomFormValues): Promise<void> => {
    const itemNo = selectedItem?.itemNo;

    if (!itemNo) {
      ToastService.error('No item selected');
      return;
    }

    if (!data.bomValidFrom) {
      ToastService.error('Please select a date');
      return;
    }

    try {
      setIsCloning(true);

      const success = await cloneVersion(data.bomValidFrom);

      if (success) {
        setIsModalOpen(false);
        handleVersionChange(data.bomValidFrom);
        await queryClient.invalidateQueries(['items']);
      }
    } catch (error) {
      console.error('Error in clone submission:', error);
      ToastService.error('Failed to clone BOM version');
    } finally {
      setIsCloning(false);
    }
  }, [selectedItem?.itemNo, cloneVersion, handleVersionChange, queryClient]);

  const handleRetry = useCallback(() => {
    if (error && error.retryable) {
      fetchVersions();
    }
  }, [error, fetchVersions]);

  if (nonVersionedItems.length !== 1) {
    return null;
  }

  const buttonTooltip = operationInProgress
    ? `Operation in progress: ${operationType || 'loading'}`
    : currentVersion
      ? `BoM valid from: ${formatBackendDateForDisplay(currentVersion)}`
      : 'Manage BoM versions';

  return (
    <Container ref={dropdownRef}>
      <StyledTooltip title={buttonTooltip}>
        <span>
          <StyledToolbarButton
            ssrIcon={historyIcon}
            text={operationInProgress ? `Loading...` : "BoM Versions"}
            iconOnly={true}
            small={true}
            onClick={handleToggleDropdown}
            disabled={operationInProgress}
          />
        </span>
      </StyledTooltip>

      <VersionsDropdown
        isOpen={isDropdownOpen}
        isLoadingVersions={isLoadingVersions}
        isLoadingVersionChange={isLoadingVersionChange}
        bomVersionHistory={bomVersionHistory}
        currentBomValidFrom={currentVersion}
        error={error}
        onRetry={handleRetry}
        onVersionSelect={(date) => {
          handleVersionChange(date);
          setIsDropdownOpen(false);
        }}
        onClearVersion={() => {
          handleClearBomVersion();
          setIsDropdownOpen(false);
        }}
        onOpenCloneModal={handleOpenCloneModal}
        handleKeyDown={handleKeyDown}
        onClose={() => {
          setIsDropdownOpen(false);
        }}
      />

      {isModalOpen && (
        <CloneBomModal
          onClose={handleCloseModal}
          onSubmit={onSubmit}
          bomVersionHistory={bomVersionHistory}
          currentBomValidFrom={currentVersion}
          isCloning={isCloning || isApiCloning}
          error={error}
          onRetry={handleRetry}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
   position: relative;
   display: inline-block;
`;
